import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import auth from "../../firebase.init";
import axios from "axios";
import UserDropdown from "./UserDropdown";

const NavBar = () => {
  const [user, loading, error] = useAuthState(auth);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [footerData, setFooterData] = useState({
    logo: { src: "", alt: "" },
  });
  const [footerLoading, setFooterLoading] = useState(true);
  const [footerError, setFooterError] = useState(null);

  // Cache keys for localStorage
  const CACHE_KEYS = {
    USER_DATA: 'navbar_user_data',
    FOOTER_DATA: 'navbar_footer_data',
    AUTH_STATE: 'navbar_auth_state'
  };

  // Load cached data on component mount
  useEffect(() => {
    const loadCachedData = () => {
      try {
        // Load cached user data
        const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
        if (cachedUserData) {
          setUserData(JSON.parse(cachedUserData));
        }

        // Load cached footer data
        const cachedFooterData = localStorage.getItem(CACHE_KEYS.FOOTER_DATA);
        if (cachedFooterData) {
          setFooterData(JSON.parse(cachedFooterData));
          setFooterLoading(false);
        }

        // Load cached auth state
        const cachedAuthState = localStorage.getItem(CACHE_KEYS.AUTH_STATE);
        if (cachedAuthState) {
          const authData = JSON.parse(cachedAuthState);
          // Only use cached auth state if it's recent (less than 5 minutes old)
          if (Date.now() - authData.timestamp < 5 * 60 * 1000) {
            setAuthLoading(false);
          }
        }
      } catch (error) {
        console.error('Error loading cached data:', error);
      }
    };

    loadCachedData();
  }, []);

  // Handle Firebase auth state changes
  useEffect(() => {
    if (!loading) {
      setAuthLoading(false);
      
      // Cache auth state
      try {
        const authStateData = {
          hasUser: !!user,
          timestamp: Date.now(),
          userEmail: user?.email || null
        };
        localStorage.setItem(CACHE_KEYS.AUTH_STATE, JSON.stringify(authStateData));
      } catch (error) {
        console.error('Error caching auth state:', error);
      }
    }
  }, [user, loading]);

  // Fetch footer data with caching
  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        setFooterLoading(true);
        const response = await axios.get(
          "https://emailserverjuly.shahlimon.online/api/footer-section"
        );
        if (response.data.success) {
          setFooterData(response.data.data);
          
          // Cache footer data
          try {
            localStorage.setItem(CACHE_KEYS.FOOTER_DATA, JSON.stringify(response.data.data));
          } catch (error) {
            console.error('Error caching footer data:', error);
          }
        } else {
          setFooterError("Failed to fetch footer data");
        }
      } catch (err) {
        console.error("Error fetching footer data:", err);
        setFooterError("Error fetching footer data");
      } finally {
        setFooterLoading(false);
      }
    };

    // Only fetch if we don't have cached data or if it's been more than 10 minutes
    const cachedFooterData = localStorage.getItem(CACHE_KEYS.FOOTER_DATA);
    const shouldFetch = !cachedFooterData || 
      (Date.now() - (JSON.parse(localStorage.getItem('footer_data_timestamp') || '0')) > 10 * 60 * 1000);

    if (shouldFetch) {
      fetchFooterData();
    }
  }, []);

  // Fetch user data with caching
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.email) {
        setUserData(null);
        // Clear cached user data when no user
        try {
          localStorage.removeItem(CACHE_KEYS.USER_DATA);
        } catch (error) {
          console.error('Error clearing cached user data:', error);
        }
        return;
      }

      try {
        const response = await fetch(
          `https://emailserverjuly.shahlimon.online/users?userEmail=${user.email}`
        );
        const data = await response.json();
        
        if (data && data.length > 0) {
          const userData = data[0];
          setUserData(userData);
          
          // Cache user data
          try {
            localStorage.setItem(CACHE_KEYS.USER_DATA, JSON.stringify(userData));
          } catch (error) {
            console.error('Error caching user data:', error);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    // Only fetch user data if we have a user and either no cached data or cached data is stale
    if (user?.email) {
      const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
      const shouldFetch = !cachedUserData || !userData;
      
      if (shouldFetch) {
        fetchUserData();
      }
    }
  }, [user]);

  const handleSignout = async () => {
    try {
      await signOut(auth);
      
      // Clear all cached data on logout
      Object.values(CACHE_KEYS).forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error clearing ${key} from localStorage:`, error);
        }
      });
      
      setUserData(null);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuVisible(!mobileMenuVisible);
    if (!mobileMenuVisible) {
      document.body.classList.add("mobile-menu-visible");
    } else {
      document.body.classList.remove("mobile-menu-visible");
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuVisible(false);
    document.body.classList.remove("mobile-menu-visible");
  };

  // Close mobile menu when navigating to a new route
  useEffect(() => {
    return () => {
      document.body.classList.remove("mobile-menu-visible");
    };
  }, []);

  // Show loading state while auth is being determined
  if (authLoading || loading) {
    return (
      <header className="main-header main-header-one">
        <div className="header-lower">
          <div className="main-menu__wrapper">
            <div className="inner-container d-flex align-items-center justify-content-between">
              <div className="main-header-one__logo-box">
               
              </div>
              <div className="nav-outer">
                <nav className="main-menu show navbar-expand-md">
                  <div className="navbar-header">
                    <button
                      className="navbar-toggler"
                      type="button"
                      data-toggle="collapse"
                      data-target="#navbarSupportedContent"
                      aria-controls="navbarSupportedContent"
                      aria-expanded="false"
                      aria-label="Toggle navigation"
                    >
                      <span className="icon-bar" />
                      <span className="icon-bar" />
                      <span className="icon-bar" />
                    </button>
                  </div>
                </nav>
              </div>
              <div className="auth-loading d-flex align-items-center">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                  <span className="visually-hidden"></span>
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      <header className="main-header main-header-one">
        {/* Header Lower */}
        <div className="header-lower">
          <div className="main-menu__wrapper">
            <div className="inner-container d-flex align-items-center justify-content-between">
              {/* Logo Box */}
              <div className="main-header-one__logo-box">
               
              </div>
              <div className="nav-outer">
                {/* Main Menu */}
                <nav className="main-menu show navbar-expand-md">
                  <div className="navbar-header">
                    <button
                      className="navbar-toggler"
                      type="button"
                      data-toggle="collapse"
                      data-target="#navbarSupportedContent"
                      aria-controls="navbarSupportedContent"
                      aria-expanded="false"
                      aria-label="Toggle navigation"
                    >
                      <span className="icon-bar" />
                      <span className="icon-bar" />
                      <span className="icon-bar" />
                    </button>
                  </div>
                </nav>
                {/* Main Menu End*/}
              </div>
              <UserDropdown
                user={user}
                userData={userData}
                handleSignout={handleSignout}
                toggleMobileMenu={toggleMobileMenu}
              />
              {/* End Outer Box */}
            </div>
          </div>
        </div>
        {/* End Header Lower */}
        {/* Mobile Menu  */}
        <div
          className={`mobile-menu ${
            mobileMenuVisible ? "mobile-menu-visible" : ""
          }`}
        >
          <div className="menu-backdrop" onClick={closeMobileMenu} />
          <div className="close-btn" onClick={closeMobileMenu}>
            <span className="icon far fa-times fa-fw" />
          </div>
          <nav className="menu-box">
            <div className="nav-logo">
              <Link to="/" onClick={closeMobileMenu}>
                <img src={footerData.logo.src || "https://attherate.dev/host-image/uploads/1748934672_HuntCrow-Logo-1.png"} alt={footerData.logo.alt || "Logo"} title="Logo" />
              </Link>
            </div>
            {/* Search */}

            <div className="menu-outer">
              {/* Mobile Menu */}
              <ul className="navigation clearfix">
                <li className="dropdown">
                  <Link to="/" onClick={closeMobileMenu}>
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" onClick={closeMobileMenu}>
                    About
                  </Link>
                </li>

                <li>
                  <Link to="/contact" onClick={closeMobileMenu}>
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            {/* User Account for Mobile */}
            {user ? (
              <div className="mobile-user-account">
                <p>Hi, {userData?.userName || user.email}</p>
                <Link
                  onClick={() => {
                    handleSignout();
                    closeMobileMenu();
                  }}
                  className="border border-secondary px-3 py-2 rounded d-inline-block ms-3"
                >
                  Logout
                </Link>
              </div>
            ) : (
              <div className="mobile-user-account">
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="border border-secondary px-3 py-2 rounded d-inline-block ms-3"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  onClick={closeMobileMenu}
                  className="border border-secondary px-3 py-2 rounded d-inline-block ms-3"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </nav>
        </div>
        {/* End Mobile Menu */}
      </header>
    </>
  );
};

export default NavBar;